import { computed } from 'vue'
import { useRequirementsStore } from '@/views/Requirements/store'
import type { Requirement, DevelopmentStatus } from '@/types'
import type { BacklogState } from './state'

const ALL_DEV_STATUSES: DevelopmentStatus[] = [
  'backlog',
  'design',
  'development',
  'code_review',
  'qa',
  'uat',
  'production',
]

export const createGetters = (state: BacklogState) => {
  const requirementsStore = useRequirementsStore()

  const activeSprint = computed(() => {
    return state.sprints.find((s) => s.status === 'active') || null
  })

  const planningSprints = computed(() => {
    return state.sprints.filter((s) => s.status === 'planning')
  })

  const completedSprints = computed(() => {
    return state.sprints
      .filter((s) => s.status === 'completed')
      .sort((a, b) => {
        const dateA = a.completedAt ? new Date(a.completedAt).getTime() : 0
        const dateB = b.completedAt ? new Date(b.completedAt).getTime() : 0
        return dateB - dateA
      })
  })

  const selectedSprint = computed(() => {
    if (!state.selectedSprintId) return null
    return state.sprints.find((s) => s.id === state.selectedSprintId) || null
  })

  const assignableSprints = computed(() => {
    return state.sprints.filter((s) => s.status === 'planning' || s.status === 'active')
  })

  const filteredBacklogRequirements = computed(() => {
    let result = [...requirementsStore.requirements]

    // Filter by search
    if (state.filters.search) {
      const search = state.filters.search.toLowerCase()
      result = result.filter(
        (req) =>
          req.title.toLowerCase().includes(search) ||
          req.key?.toLowerCase().includes(search) ||
          req.description?.toLowerCase().includes(search)
      )
    }

    // Filter by project
    if (state.filters.projectId) {
      result = result.filter((req) => req.projectId === state.filters.projectId)
    }

    // Filter by application
    if (state.filters.applicationId) {
      result = result.filter((req) => req.applicationId === state.filters.applicationId)
    }

    // Filter by type
    if (state.filters.type) {
      result = result.filter((req) => req.type === state.filters.type)
    }

    // Filter by priority
    if (state.filters.priority) {
      result = result.filter((req) => req.priority === state.filters.priority)
    }

    // Filter by definition status
    if (state.filters.definitionStatus) {
      result = result.filter((req) => req.definitionStatus === state.filters.definitionStatus)
    }

    // Filter by development status
    if (state.filters.developmentStatus) {
      result = result.filter((req) => req.developmentStatus === state.filters.developmentStatus)
    }

    // Filter by sprint
    if (state.filters.sprintId === 'none') {
      result = result.filter((req) => !req.sprintId)
    } else if (state.filters.sprintId) {
      result = result.filter((req) => req.sprintId === state.filters.sprintId)
    }

    // Filter by assigned to
    if (state.filters.assignedTo) {
      result = result.filter((req) => req.assignedTo === state.filters.assignedTo)
    }

    // Filter by label
    if (state.filters.label) {
      result = result.filter((req) => req.labels?.includes(state.filters.label))
    }

    return result
  })

  const getSprintRequirements = (sprintId: string): Requirement[] => {
    return requirementsStore.requirements.filter((req) => req.sprintId === sprintId)
  }

  const getSprintRequirementsByDevStatus = (sprintId: string): Record<DevelopmentStatus, Requirement[]> => {
    const grouped: Record<DevelopmentStatus, Requirement[]> = {
      backlog: [],
      design: [],
      development: [],
      code_review: [],
      qa: [],
      uat: [],
      production: [],
    }

    requirementsStore.requirements
      .filter((req) => req.sprintId === sprintId)
      .forEach((req) => {
        if (req.developmentStatus && grouped[req.developmentStatus]) {
          grouped[req.developmentStatus].push(req)
        }
      })

    // Sort each column by boardOrder (items without boardOrder go to the end)
    for (const status of ALL_DEV_STATUSES) {
      grouped[status].sort((a, b) => (a.boardOrder ?? Infinity) - (b.boardOrder ?? Infinity))
    }

    return grouped
  }

  const unassignedRequirements = computed(() => {
    return requirementsStore.requirements.filter((req) => !req.sprintId)
  })

  const getSprintStats = (sprintId: string) => {
    const sprint = state.sprints.find((s) => s.id === sprintId)
    if (!sprint) return { totalSP: 0, completedSP: 0, totalReqs: 0, completedReqs: 0, progress: 0 }

    const reqs = getSprintRequirements(sprintId)
    const totalSP = reqs.reduce((sum, r) => sum + (r.storyPoints || 0), 0)
    const completedSP = reqs
      .filter((r) => r.developmentStatus === 'production')
      .reduce((sum, r) => sum + (r.storyPoints || 0), 0)
    const totalReqs = reqs.length
    const completedReqs = reqs.filter((r) => r.developmentStatus === 'production').length
    const progress = totalSP > 0 ? Math.round((completedSP / totalSP) * 100) : 0

    return { totalSP, completedSP, totalReqs, completedReqs, progress }
  }

  const availableLabels = computed(() => {
    const labelsSet = new Set<string>()
    requirementsStore.requirements.forEach((req) => {
      req.labels?.forEach((label) => labelsSet.add(label))
    })
    return Array.from(labelsSet).sort()
  })

  const historicalVelocity = computed(() => {
    const completed = completedSprints.value
    if (completed.length === 0) return 0

    const totalCompletedSP = completed.reduce((sum, s) => sum + (s.completedStoryPoints || 0), 0)
    return Math.round(totalCompletedSP / completed.length)
  })

  return {
    activeSprint,
    planningSprints,
    completedSprints,
    selectedSprint,
    assignableSprints,
    filteredBacklogRequirements,
    getSprintRequirements,
    getSprintRequirementsByDevStatus,
    unassignedRequirements,
    availableLabels,
    getSprintStats,
    historicalVelocity,
  }
}
